/**
 * Wire types for everything the database RPCs return.
 *
 * These describe the exact JSON produced by the functions in
 * `supabase/migrations`. They are hand-maintained rather than generated because
 * every one of these payloads is assembled by an explicit `jsonb_build_object`,
 * which the type generator cannot see through.
 */

import type { OpeningException, OpeningHour } from './hours';
import type { Order } from './order';

export interface RestaurantPublic {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  phone: string | null;
  address: string | null;
  timezone: string;
  currency: string;
  logo_url: string | null;
  cover_url: string | null;
  accepting_orders: boolean;
  tax_rate_bps: number;
  min_lead_minutes: number;
  max_advance_days: number;
  slot_interval_minutes: number;
  is_open_now: boolean;
  can_order_now: boolean;
  /*
   * When the platform marked this restaurant, or null when nobody has looked.
   *
   * A timestamp rather than a boolean because the mark is a moment: the panel
   * says "verified since September 2026", and a shop that was marked years ago
   * should look like one.
   */
  verified_since: string | null;
  current_window_start: string | null;
  current_window_end: string | null;
  next_opening_at: string | null;
  hours: OpeningHour[];
  exceptions: OpeningException[];
}

export interface ScanResolution {
  kind: 'table' | 'link';
  table_id: string | null;
  table_label: string | null;
  restaurant_id: string;
  slug: string;
}

export interface MenuItemAvailabilityWindow {
  id?: string;
  weekday: number;
  start_time: string;
  end_time: string;
}

export interface PublicMenuItem {
  id: string;
  name: string;
  /**
   * The options this dish comes in. Empty for a dish sold whole, in which case
   * `price_cents` is what is charged.
   */
  /** The groups this dish offers. Always present, empty when it offers none. */
  option_groups: PublicOptionGroup[];
  description: string | null;
  price_cents: number;
  /** The smallest quantity this dish can be ordered in; 1 means no minimum. */
  min_quantity: number;
  discount_cents: number;
  effective_price_cents: number;
  discount_label: string | null;
  discount_ends_at?: string | null;
  photo_url: string | null;
  sort_order: number;
  prep_minutes_baseline?: number;
  track_inventory: boolean;
  inventory_left: number | null;
  is_sold_out: boolean;
  is_available_now: boolean;
  availability_windows?: MenuItemAvailabilityWindow[];
}

/**
 * The span of a dish's option prices, in minor units.
 *
 * A dish with no options answers with its own price twice, and a dish whose
 * options all cost the same answers with that price twice — so a caller can
 * decide whether there is a range to show by comparing the two, and a range of
 * one price is never rendered as `$8.00 – $8.00`.
 *
 * Options are compared on what the customer actually pays, so a discount that
 * lands on one size and not another is reflected in the span.
 *
 * @param item A menu item, as the customer sees it.
 * @returns The lowest and highest price across its options.
 */
export function optionPriceRange(item: PublicMenuItem): { min: number; max: number } {
  // Sizes are an absolute group's choices now, each carrying the full price.
  const prices = (item.option_groups ?? [])
    .filter((group) => group.price_mode === 'absolute')
    .flatMap((group) => group.choices.map((choice) => choice.price_cents));
  if (prices.length === 0) {
    return { min: item.effective_price_cents, max: item.effective_price_cents };
  }
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

export interface PublicMenuCategory {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  items: PublicMenuItem[];
}

export interface PublicMenu {
  restaurant_id: string;
  currency: string;
  generated_at: string;
  categories: PublicMenuCategory[];
  uncategorised: PublicMenuItem[];
}

export interface MerchantCategory {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface MerchantDiscount {
  id: string;
  label: string | null;
  kind: 'percent' | 'amount';
  value: number;
  starts_at: string;
  ends_at: string;
  is_active: boolean;
  is_running_now: boolean;
}

/** A choice within a group, as the merchant's editor sees it. */
export interface MerchantOptionChoice {
  id: string;
  name: string;
  price_cents: number;
  sort_order: number;
  is_active: boolean;
}

export interface MerchantOptionGroup {
  id: string;
  name: string;
  /** 'absolute': the choice is the dish's price. 'delta': it is added to it. */
  price_mode: 'absolute' | 'delta';
  min_select: number;
  max_select: number;
  sort_order: number;
  choices: MerchantOptionChoice[];
}

export interface MerchantMenuItem {
  id: string;
  category_id: string | null;
  name: string;
  description: string | null;
  price_cents: number;
  photo_url: string | null;
  photo_path: string | null;
  sort_order: number;
  is_active: boolean;
  track_inventory: boolean;
  inventory_count: number | null;
  prep_minutes_baseline: number;
  min_quantity: number;
  availability_windows: MenuItemAvailabilityWindow[];
  discounts: MerchantDiscount[];
  /** The groups this dish offers. Always present, empty when it offers none. */
  option_groups: MerchantOptionGroup[];
}

export interface MerchantMenu {
  categories: MerchantCategory[];
  items: MerchantMenuItem[];
}

export interface MerchantRestaurant {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  phone: string | null;
  address: string | null;
  timezone: string;
  currency: string;
  logo_url: string | null;
  cover_url: string | null;
  accepting_orders: boolean;
  tax_rate_bps: number;
  min_lead_minutes: number;
  max_advance_days: number;
  slot_interval_minutes: number;
  default_prep_minutes: number;
  min_prep_minutes: number;
  max_prep_minutes: number;
  kitchen_capacity: number;
  /**
   * How long the restaurant allows before a customer is told nobody has accepted
   * their order yet.
   */
  accept_grace_seconds: number;
  /** What that window is measured from: the order landing, or the estimate. */
  accept_grace_basis: 'placed' | 'estimate';
  /**
   * How long a ready order may wait on the counter before the board calls it
   * late and tells the counter to chase the customer.
   */
  ready_chase_seconds: number;
  /**
   * How long one cooking round covers on the To cook page: orders whose ready-by
   * falls inside the window batch together.
   */
  cook_round_seconds: number;
  /** Have non-scheduled orders start in the kitchen rather than awaiting acceptance. */
  auto_accept_orders: boolean;
  is_active: boolean;
  role: 'owner' | 'manager' | 'staff';
  opening_hours: OpeningHour[];
  exceptions: OpeningException[];
}

export interface RestaurantSummary {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  currency: string;
  logo_url: string | null;
  is_active: boolean;
  accepting_orders: boolean;
  /** Non-scheduled orders start in the kitchen rather than awaiting acceptance. */
  auto_accept_orders: boolean;
  /**
   * How long one cooking round covers on the To cook page.
   *
   * Read here rather than fetched by the page that needs it: the rail's badge
   * counts the same batches, and the two must be counted in one window.
   */
  cook_round_seconds: number;
  /** When the platform marked this restaurant, or null. Settings shows it read-only. */
  verified_since: string | null;
  role: 'owner' | 'manager' | 'staff';
}

export interface RestaurantTable {
  id: string;
  label: string;
  seats: number | null;
  is_active: boolean;
  token: string;
}

/** One line of the server's authoritative cart quote. */
export interface QuoteLine {
  item_id: string;
  name: string;
  photo_url: string | null;
  unit_price_cents: number;
  quantity: number;
  discount_cents: number;
  unit_discount_cents: number;
  line_total_cents: number;
  discount_id: string | null;
  discount_label: string | null;
}

export interface OrderQuote {
  lines: QuoteLine[];
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  currency: string;
  eta_seconds: number;
  eta_ready_at: string;
  eta_model: string;
  eta_sample_count: number;
  eta_queue_depth: number;
  is_scheduled: boolean;
  /**
   * The estimate itself, in seconds: how long the basket takes to make.
   *
   * Separate from `eta_seconds`, which is the wait until the chosen slot once
   * one is chosen. This one is a fact about the basket either way, and it is the
   * number the checkout quotes when it explains why a time is too soon.
   */
  prep_seconds: number;
  /**
   * The soonest pickup this basket can make, as an instant — the server's `now`
   * plus {@link prep_seconds}.
   *
   * An instant rather than a duration, and the server's clock rather than the
   * client's, so the checkout draws its "too soon" line without doing the
   * arithmetic and without a second opinion about what now is. Informational:
   * nothing refuses a slot inside it.
   */
  earliest_scheduled_for: string;
}

/** A cart line as held on the device, before the server prices it. */
/** One choice within a group, as the customer's menu offers it. */
export interface PublicOptionChoice {
  id: string;
  name: string;
  /**
   * For a delta group, the surcharge to add. For an absolute group, what this
   * choice makes the dish cost — the number the customer reads, discount and all.
   */
  price_cents: number;
  price_mode: 'absolute' | 'delta';
}

export interface PublicOptionGroup {
  id: string;
  name: string;
  /** 'absolute': the choice is the dish's price. 'delta': it is added to it. */
  price_mode: 'absolute' | 'delta';
  min_select: number;
  max_select: number;
  /** Only the active choices; a group with none is not sent at all. */
  choices: PublicOptionChoice[];
}

/** A choice the customer made, kept on the line so the price can be rebuilt. */
export interface CartOption {
  choiceId: string;
  groupName: string;
  name: string;
  priceCents: number;
  /** True when this choice is the dish's price rather than a surcharge. */
  isAbsolute: boolean;
  /** Always 1 today: the menu offers a toggle, not a stepper. */
  quantity: number;
}

export interface CartLine {
  itemId: string;
  name: string;
  unitPriceCents: number;
  quantity: number;
  /** The smallest quantity this line may hold; 1 means no minimum. */
  minQuantity: number;
  photoUrl: string | null;
  /**
   * What was chosen from the dish's groups. Optional: the original customer app
   * predates groups and its lines simply have none.
   */
  options?: CartOption[];
}

/**
 * A cart line's identity: the dish and the option together.
 *
 * Small and Large of one dish are two lines, so only identical choices merge.
 *
 * @param itemId The dish.
 * @param variantId The option, or null.
 * @returns A key unique within a cart.
 */
export function cartLineKey(itemId: string, options: CartOption[] = []): string {
  // Sorted, so the same choices picked in a different order are still one line:
  // otherwise a customer could accumulate four identical lines.
  const chosen = options.map((option) => option.choiceId).sort().join(',');
  return `${itemId}:${chosen}`;
}

/**
 * The price a set of choices makes.
 *
 * An absolute choice replaces the base — that is what the group means — and the
 * deltas still add on top, because a pizza has both a size and extra toppings.
 * Deliberately the same rule `compute_cart` applies, so the cart never shows a
 * price the server would not charge.
 *
 * @param base The dish's own price, or its chosen portion's.
 * @param options The choices made.
 * @returns The unit price in minor units.
 */
export function withOptions(base: number, options: CartOption[]): number {
  const absolute = options.find((option) => option.isAbsolute);
  const deltas = options.reduce(
    (sum, option) => (option.isAbsolute ? sum : sum + option.priceCents),
    0
  );
  return (absolute ? absolute.priceCents : base) + deltas;
}

export interface DashboardSummary {
  orders_placed: number;
  orders_completed: number;
  orders_cancelled: number;
  orders_open: number;
  orders_scheduled: number;
  net_sales_cents: number;
  booked_sales_cents: number;
  discounts_cents: number;
  tax_cents: number;
  gross_sales_cents: number;
  avg_order_cents: number;
  avg_total_seconds: number;
  median_total_seconds: number;
  p90_total_seconds: number;
  avg_accept_seconds: number;
  avg_kitchen_seconds: number;
  avg_handover_seconds: number;
  eta_mae_seconds: number;
  on_time_count: number;
  measured_count: number;
}

export interface DashboardDay {
  day: string;
  orders_placed: number;
  orders_completed: number;
  orders_cancelled: number;
  net_sales_cents: number;
  avg_total_seconds: number;
}

export interface DashboardHour {
  hour: number;
  orders_placed: number;
  net_sales_cents: number;
}

export interface DashboardTopItem {
  name: string;
  item_id: string | null;
  quantity: number;
  revenue_cents: number;
}

export interface DashboardStatusCount {
  status: string;
  count: number;
}

export interface DashboardStats {
  restaurant_id: string;
  timezone: string;
  currency: string;
  from: string;
  to: string;
  summary: DashboardSummary;
  on_time_rate: number | null;
  series: DashboardDay[];
  by_hour: DashboardHour[];
  top_items: DashboardTopItem[];
  status_breakdown: DashboardStatusCount[];
}

export type DashboardRange =
  | 'today'
  | 'yesterday'
  | 'week'
  | 'last_28_days'
  | 'month'
  | 'last_month'
  | 'year';

/**
 * Sums a run of daily totals into calendar months.
 *
 * The month comes from the date key rather than from a date: the key is already
 * the shop's own day, so slicing it is the month the shop traded in, with no zone
 * to apply a second time. Months keep the order the days arrived in — ascending,
 * a `generate_series` ordered by day — because a `Map` keeps what it is given,
 * and a chart reads along its axis.
 *
 * @param series The dashboard's daily rows, one per day of the window.
 * @returns One entry per month touched, keyed `YYYY-MM`, in cents.
 */
export function monthlySales(series: DashboardDay[]): Array<{ key: string; cents: number }> {
  const months = new Map<string, number>();
  for (const day of series) {
    const month = day.day.slice(0, 7);
    months.set(month, (months.get(month) ?? 0) + day.net_sales_cents);
  }
  return [...months].map(([key, cents]) => ({ key, cents }));
}

/**
 * The window a dashboard is read over.
 *
 * A preset is resolved on the server, in the shop's own timezone — "this month"
 * is the shop's month, not the reader's, and that arithmetic already lives in
 * one place. A custom window carries its own two days instead, taken from the
 * calendar the merchant picked them on, which is also the shop's.
 */
export type DashboardWindow =
  | { kind: 'preset'; range: DashboardRange }
  | { kind: 'custom'; from: string; to: string };

/** One dish the model has timed often enough to rank. */
export interface SlowestItem {
  name: string;
  median_seconds: number;
  /** How many measured orders the median is over — at least three, server-side. */
  samples: number;
}

export interface EtaInsights {
  sample_count: number;
  window_days: number;
  avg_prep_seconds: number;
  median_prep_seconds: number;
  p90_prep_seconds: number;
  last_sample_at: string | null;
  is_learning: boolean;
  /**
   * The average signed miss: positive means the order took longer than quoted, so
   * the estimate ran short. The dashboard's `eta_mae_seconds` is the same average
   * with the sign thrown away, and size without direction is half the fact.
   */
  estimate_bias_seconds: number;
  /** How many measured orders that average is over; zero means say nothing. */
  estimate_sample_count: number;
  /** The dishes with the longest median prep, longest first. */
  slowest_items: SlowestItem[];
}

/**
 * Arrivals in one hour of the shop's own day.
 *
 * `per_hour` is the average over the days that hour actually had orders, so a
 * single busy Friday cannot dilute a weekday's ordinary rate.
 */
export interface CookArrivalHour {
  /** 0–23, in the restaurant's timezone. */
  hour: number;
  /** Every order placed in this hour across the whole window. */
  orders: number;
  /** Average orders in this hour, over the days it had any. */
  per_hour: number;
}

/**
 * What the kitchen's own record says about its capacity.
 *
 * Two sources, deliberately. The waiting/cooking split is read from
 * `cook_rounds` — real batches only, never a ticket marked from the by-order
 * view — and the arrival rate from `orders.placed_at`, which is a fact about the
 * queue rather than about a batch. See `docs/cook-rounds.md`.
 */
export interface CookInsights {
  window_days: number;
  /** Real batches logged. Zero means every measured column below is zero. */
  round_count: number;
  /** Orders that came off in those batches. */
  order_count: number;
  median_batch_size: number;
  p90_batch_size: number;
  last_round_at: string | null;
  /** Holding the batch for volume. The price of a fuller wok. */
  median_batch_wait_seconds: number;
  /** Waiting for a free burner. Not waste — nothing could have started sooner. */
  median_capacity_wait_seconds: number;
  mean_cook_seconds: number;
  median_cook_seconds: number;
  p90_cook_seconds: number;
  /**
   * E[S²], the second moment of cook time **about zero**, which is the form the
   * queueing formula uses. Subtract `mean_cook_seconds²` for the variance.
   */
  mean_cook_square_seconds2: number;
  /** All 24 hours, zero-filled, for the strip. */
  arrivals_by_hour: CookArrivalHour[];
  peak_hour: number;
  peak_orders_per_hour: number;
  mean_orders_per_hour: number;
  service_days: number;
  /** Fewer than ten batches: say so rather than imply precision. */
  is_learning: boolean;
  kitchen_capacity: number;
  cook_round_seconds: number;
  default_prep_seconds: number;
  min_prep_seconds: number;
  max_prep_seconds: number;
}

/**
 * The live policy for cutting the next round.
 *
 * Inputs rather than a decision, deliberately. The wait that decides a cutoff
 * grows with the clock, so an answer computed server-side is stale the moment it
 * is read; the To cook page holds the queue over realtime and has a live clock,
 * so it decides with `roundCutoff`. This is what it decides from.
 */
export interface CookQueue {
  stations: number;
  cook_round_seconds: number;
  /** What this shop cooks at a time, from its own logged rounds. */
  target_batch: number;
  /** The longest wait the shop's round window already tolerates. */
  max_wait_seconds: number;
  /** Rounds logged in the window; below five the target batch is the floor. */
  logged_batches: number;
  /** Walk-up orders placed in the last hour. */
  arrivals_per_hour: number;
  /** The utilisation those arrivals imply, at this shop's cook time. */
  load: number;
}

/** Health of the transactional email pipeline for one restaurant. */
export interface EmailStatus {
  /** True when an API key is installed; false means receipts are only queued. */
  configured: boolean;
  queued: number;
  sent: number;
  failed: number;
  skipped: number;
  last_error: string | null;
  last_sent_at: string | null;
}

/** Everything the customer tracking screen needs, from one RPC. */
export interface OrderBundle {
  order: Order;
  restaurant: RestaurantPublic | null;
}
